import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { CycloneDXVersion, SPDXVersion } from '../../../../shared/model-typings/sbomrule';
import { selectOptionFactory } from '../../../utils';
import { isNTIAChecked, isValidMinCycloneDXVersion, isValidMinSPDXVersion } from '../../../utils/SBOMPolicyUtils';
import LabeledCheckbox from '../../generic/Forms/LabeledCheckbox';
import Select from '../../generic/Select/Select';
import Text from '../../generic/Text';
import Toggle from '../../generic/Toggle';
import type { SBOMRule } from '../../../../shared/model-typings/sbomrule';
import type { SelectOption } from '../../../types';

import styles from './DisplaySBOMPolicyPage.module.less';

type RequiredFieldsComponentProps = {
  rule: SBOMRule;
  handleUpdate: (newRule: Partial<SBOMRule>) => void;
};

const cycloneDXVersions: Array<SelectOption<CycloneDXVersion>> = Object.values(CycloneDXVersion || [])
  .map(version =>
    selectOptionFactory(version, _.upperFirst(version as string)));

const spdxVersions: Array<SelectOption<SPDXVersion>> = Object.values(SPDXVersion || [])
  .map(version =>
    selectOptionFactory(version, _.upperFirst(version as string)));

const RuleFieldsComponent: VoidFunctionComponent<RequiredFieldsComponentProps> = ({
  rule,
  handleUpdate,
}) => {
  const handleNTIAToggle = (isChecked: boolean) => {
    handleUpdate({
      componentSupplier: isChecked,
      author: isChecked,
      creationTimestamp: isChecked,
      componentPurl: isChecked,
      ...attributesToUpdateSPDX(isChecked),
      ...attributesToUpdateCycloneDX(isChecked),
    });
  };

  /**
   * Updates the CycloneDX attributes based on the checkbox state.
   *
   *
   * Default Values:
   * - `minCycloneDXVersion`: Default is `CycloneDXVersion.DENIED` when the checkbox is unchecked.
   * - `cycloneDXVulnerabilities`: Default is `false` when the checkbox is unchecked.
   *
   * @param {boolean} isChecked - Indicates if the checkbox is checked.
   * @returns An object containing the updated CycloneDX attributes:
   *   - cycloneDXRelationships: Set to `true` (NTIA element) if the checkbox is checked, `false` otherwise.
   *   - cycloneDXVulnerabilities: Set to the value of `rule.cycloneDXVulnerabilities` if checked, otherwise `false`.
   *   - minCycloneDXVersion: Set to `CycloneDXVersion.V_1_2` if checked, otherwise `CycloneDXVersion.DENIED`.
   */
  const attributesToUpdateCycloneDX = (isChecked: boolean) => {
    let minCycloneDXVersion = CycloneDXVersion.DENIED;
    let isCycloneDXVulnerabilitiesEnabled = false;
    if (isChecked) {
      minCycloneDXVersion = CycloneDXVersion.V_1_2;
      isCycloneDXVulnerabilitiesEnabled = rule.cycloneDXVulnerabilities;
    }

    return {
      cycloneDXRelationships: isChecked,
      cycloneDXVulnerabilities: isCycloneDXVulnerabilitiesEnabled,
      minCycloneDXVersion,
    };
  };

  /**
   * Updates the SPDX attributes based on the checkbox state.
   *
   * Default Values:
   * - `minSPDXVersion`: Default is `SPDXVersion.DENIED` when the checkbox is unchecked.
   * - `spdxRelationships`: Default is `false` when the checkbox is unchecked.
   *
   * @param {boolean} isChecked - Indicates if the checkbox is checked.
   * @returns An object containing the updated SPDX attributes:
   *   - spdxRelationships: Set to `true` (NTIA element) if the checkbox is checked, `false` otherwise.
   *   - minSPDXVersion: Set to `SPDXVersion.V_2_2` if checked, otherwise `SPDXVersion.DENIED`.
   */
  const attributesToUpdateSPDX = (isChecked: boolean) => {
    let minSPDXVersion = SPDXVersion.DENIED;
    if (isChecked) {
      minSPDXVersion = SPDXVersion.V_2_2;
    }

    return {
      spdxRelationships: isChecked,
      minSPDXVersion,
    };
  };

  return (
    <div className={`${styles.contentContainer}`}>
      <div className={`${styles.sectionTitle}`}>Required Fields</div>
      <div className={styles.toggleContainerFormGroup}>
        <Toggle
          checked={isNTIAChecked(rule) || false}
          onChange={(isChecked) => {
            handleNTIAToggle(isChecked);
          }}
          icons={false}
        />
        <Text.Text>
          NTIA/FDA
        </Text.Text>
      </div>

      <div className={`${styles.formText}`}>This toggles the fields below to meet the minimum requirements.
        Currently the FDA follows the NTIA requirements.
      </div>

      <div className={`${styles.fieldsGroupTitle}`}>SBOM Metadata</div>
      <LabeledCheckbox
        label="SBOM Author"
        isChecked={!!rule?.author}
        onChange={(_event, isChecked) => {
          handleUpdate({
            author: isChecked,
          });
        }}
        className={styles.checkboxContainer}
      />
      <LabeledCheckbox
        label="Creation Timestamp"
        isChecked={!!rule?.creationTimestamp}
        onChange={(_event, isChecked) => {
          handleUpdate({
            creationTimestamp: isChecked,
          });
        }}
        className={styles.checkboxContainer}
      />

      <div className={`${styles.fieldsGroupTitle}`}>Components</div>
      <LabeledCheckbox
        label="Name"
        isChecked
        isDisabled
        className={styles.checkboxContainer}
      />
      <div className={`${styles.formText}`}>Required by FOSSA. Cannot be disabled.</div>

      <LabeledCheckbox
        label="Version"
        isChecked
        isDisabled
        className={styles.checkboxContainer}
      />
      <div className={`${styles.formText}`}>Required by FOSSA. Cannot be disabled.</div>

      <LabeledCheckbox
        label="Supplier"
        isChecked={!!rule?.componentSupplier}
        onChange={(_event, isChecked) => {
          handleUpdate({
            componentSupplier: isChecked,
          });
        }}
        className={styles.checkboxContainer}
      />

      <LabeledCheckbox
        label="PURL (Package URL)"
        isChecked={!!rule?.componentPurl}
        onChange={(_event, isChecked) => {
          handleUpdate({
            componentPurl: isChecked,
          });
        }}
        className={styles.checkboxContainer}
      />
      <div className={`${styles.formText}`}>This value helps FOSSA accurately identify the component.
        Licensing and vulnerability data will be lower-quality without a valid PURL.
      </div>

      <LabeledCheckbox
        label="Generic PURL"
        isChecked={!!rule?.componentGenericPurl}
        onChange={(_event, isChecked) => {
          handleUpdate({
            componentGenericPurl: isChecked,
          });
        }}
        className={styles.checkboxContainer}
      />
      <div className={`${styles.formText}`}>Check for excessive generic Package URLs (pkg:generic).
        Flags SBOMs with more than 5 generic PURLs as potential quality issues.
      </div>

      <LabeledCheckbox
        label="CPE (Common Platform Enumeration)"
        isChecked={!!rule?.componentCPE}
        onChange={(_event, isChecked) => {
          handleUpdate({
            componentCPE: isChecked,
          });
        }}
        className={styles.checkboxContainer}
      />

      <div className={`${styles.sectionTitle}`}>File format</div>
      <div className={`${styles.formText}`}>
        At least one supported format must be required. Both formats can be required.
      </div>

      <div className={`${styles.fieldsGroupTitle}`}>CycloneDX</div>
      <div className={styles.toggleContainerFormGroup}>
        <Toggle
          checked={isValidMinCycloneDXVersion(rule)}
          onChange={(isChecked: boolean) => {
            handleUpdate(attributesToUpdateCycloneDX(isChecked));
          }}
          icons={false}
        />
        <Text.Text>
          CycloneDX allowed
        </Text.Text>
      </div>

      <div className={styles.selectContainerFormGroup}>
        {rule.minCycloneDXVersion &&
          <>
            <Text.H4>Minimal CycloneDX version</Text.H4>
            <Select
              id="minCycloneDXVersion"
              smooth="left"
              options={cycloneDXVersions}
              onChange={(version) => {
                if (version) {
                  handleUpdate({
                    minCycloneDXVersion: version.value,
                  });
                }
              }}
              className={styles.selectContainer}
              isDisabled={!isValidMinCycloneDXVersion(rule)}
              value={selectOptionFactory(rule.minCycloneDXVersion, _.upperFirst(rule.minCycloneDXVersion as string))}

            />
          </>}

        <LabeledCheckbox
          label="Dependencies"
          isChecked={!!rule?.cycloneDXRelationships}
          isDisabled={!isValidMinCycloneDXVersion(rule)}
          onChange={(_event, isChecked) => {
            handleUpdate({
              cycloneDXRelationships: isChecked,
            });
          }}
          className={styles.checkboxContainer}
        />
        <div className={`${styles.formText}`}>
          Essential for FOSSA to determine direct vs transitive dependencies.
        </div>

        <LabeledCheckbox
          label="Vulnerabilities"
          isChecked={!!rule?.cycloneDXVulnerabilities}
          isDisabled={!isValidMinCycloneDXVersion(rule)}
          onChange={(_event, isChecked) => {
            handleUpdate({
              cycloneDXVulnerabilities: isChecked,
            });
          }}
          className={styles.checkboxContainer}
        />
      </div>

      <div className={`${styles.fieldsGroupTitle}`}>SPDX</div>
      <div className={styles.toggleContainerFormGroup}>
        <Toggle
          checked={isValidMinSPDXVersion(rule)}
          onChange={(isChecked: boolean) => {
            handleUpdate(attributesToUpdateSPDX(isChecked));
          }}
          icons={false}
        />
        <Text.Text>
          SPDX allowed
        </Text.Text>
      </div>

      <div className={`${styles.selectContainerFormGroup}`}>
        {rule.minSPDXVersion &&
          <>
            <Text.H4>Minimal SPDX version</Text.H4>
            <Select
              id="minSPDXVersion"
              smooth="left"
              options={spdxVersions}
              onChange={(version) => {
                if (version) {
                  handleUpdate({
                    minSPDXVersion: version.value,
                  });
                }
              }}
              className={styles.selectContainer}
              isDisabled={!isValidMinSPDXVersion(rule)}
              value={selectOptionFactory(rule.minSPDXVersion, _.upperFirst(rule.minSPDXVersion as string))}
            />
          </>}

        <LabeledCheckbox
          label="Relationships"
          isChecked={!!rule?.spdxRelationships}
          isDisabled={!isValidMinSPDXVersion(rule)}
          onChange={(_event, isChecked) => {
            handleUpdate({
              spdxRelationships: isChecked,
            });
          }}
          className={styles.checkboxContainer}
        />
        <div className={`${styles.formText}`}>
          Essential for FOSSA to determine direct vs transitive dependencies.
        </div>
      </div>
    </div>
  );
};

export default RuleFieldsComponent;
